import { useMemo, useState } from 'react';

import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Input } from '@repo/videa-components/src/ui/input';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import { DataTable } from 'node_modules/@repo/videa-components/src/ui/data-table';

import type { THygieneInsightsTableData } from '../../components/HygieneTableColumns';
import { useGetHygieneInsightsTableColumns } from '../../components/HygieneTableColumns';
import { InsightsExportButton } from '../../components/InsightsExportButton';
import type { THygienePracticeRates } from '../../types';

export const HygieneRegionTable = ({
  practices,
  regionName,
}: {
  practices?: THygienePracticeRates[];
  regionName?: string;
}) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = useGetHygieneInsightsTableColumns({
    drilldownLevel: 'Practice',
  });

  const data: THygieneInsightsTableData[] = useMemo(() => {
    return (
      practices?.map((practice) => ({
        production: practice.production,
        opportunity: practice.opportunity,
        productionRatio: practice.productionRatio,
        productionRatioChange: practice.productionRatioChange,
        estimatedHygieneValues: practice.estimatedHygieneValues,
        id: practice.practiceId,
        name: practice.practiceName,
      })) ?? []
    );
  }, [practices]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    state: {
      sorting,
      columnFilters,
    },
  });
  return (
    <div className="vai-flex vai-flex-col vai-gap-4 vai-w-full">
      <div className="vai-flex vai-flex-row vai-justify-between vai-items-center">
        <span className="vai-typography-large">Practices</span>
        <div className="vai-flex vai-flex-row vai-gap-4 vai-items-center">
          <div className="vai-relative">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="vai-absolute vai-left-3 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-text-gray-400 vai-z-10"
            />
            <Input
              placeholder="Search Practices"
              value={
                (table.getColumn('name')?.getFilterValue() as string) ?? ''
              }
              onChange={(event) =>
                table.getColumn('name')?.setFilterValue(event.target.value)
              }
              className="vai-w-full vai-pl-10 vai-pr-8"
            />
          </div>
          <InsightsExportButton
            table={table}
            name={regionName}
            isDisabled={!data?.length}
          />
        </div>
      </div>
      <DataTable table={table} className="vai-h-[calc(100vh-400px)]" />
    </div>
  );
};
